import { Tab } from '@headlessui/react';

import ModuleTabs from '@/components/common/ModuleTab';
import HistoricSearchModal from '@/components/modal/HistoricSearchModal';
import OrderBlotterTable from '@/components/modules/Blotters/OrderBlotterTable';
import TradeBlotterTable from '@/components/modules/Blotters/TradeBlotterTable';
import { getEnvironment, shouldUseNopBalance, shouldUseNopProfitLoss } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { generatePair } from '@/state/reducers/marketPairSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { setActiveTicker } from '@/state/reducers/tickerSlice';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import useLanguage from '@/utils/hooks/useLanguage';
import { Fragment, useCallback, useMemo, useState } from 'react';
import NopPositionTable from './NopPositionTable';
import OpenPositionTable, { OpenPositionRow } from './OpenPositionTable';

interface TraderBlottersProps {
    setCSVData: React.Dispatch<React.SetStateAction<any[]>>;
}

export default function Blotters({ setCSVData }: TraderBlottersProps) {
    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const [opened, handlers] = useDisclosure(false);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const [prefillValues, setPrefillValues] = useState<OrderPrefillValues>();

    const isMarginAccount = useMemo(() => {
        if (currentAccount && credentials) {
            const accountProperties = credentials.accounts.find(
                (account) => account.code === currentAccount
            )?.properties;
            return accountProperties?.find((property) => property.key === 'TYPE')?.value === 'MARGIN';
        }
    }, [currentAccount, credentials]);

    const tabs = useMemo(() => {
        const baseTabs = [lang.blotters.orders, lang.blotters.trades];
        if (getEnvironment() === 'uat') {
            return [...baseTabs, `${lang.blotters.openPositions} (Celer)`, `${lang.blotters.openPositions} (GCEX)`];
        } else {
            return [...baseTabs, lang.blotters.openPositions];
        }
    }, [lang, isMarginAccount]);

    const orderDisclosure = useDisclosure(false);

    const onOrderModal = useCallback(
        (row: OpenPositionRow) => {
            orderDisclosure[1].open();
            const market = generatePair(row.market.celer);
            dispatch(setActiveTicker(market));
            setPrefillValues({
                instrument: market,
                netQtyPosition: row.netQtyPosition,
                price: row.currentPrice
            });
        },
        [orderDisclosure]
    );

    return (
        <div className="w-full h-full flex flex-col">
            <HistoricSearchModal opened={opened} handlers={handlers} />
            <Tab.Group defaultIndex={3}>
                <ModuleTabs.Main tabs={tabs} className="pb-2" />
                <Tab.Panels className="relative self-stretch grow basis-full p-2 overflow-hidden">
                    <Tab.Panel className="h-full">
                        <OrderBlotterTable setCSVData={setCSVData} />
                    </Tab.Panel>
                    <Tab.Panel className="h-full">
                        <TradeBlotterTable setCSVData={setCSVData} />
                    </Tab.Panel>
                    {getEnvironment() === 'uat' ? (
                        <Fragment>
                            <Tab.Panel className="h-full">
                                <OpenPositionTable
                                    setCSVData={setCSVData}
                                    onOrderModal={onOrderModal}
                                    prefillValues={prefillValues}
                                    setPrefillValues={setPrefillValues}
                                    orderDisclosure={orderDisclosure}
                                />
                            </Tab.Panel>
                            <Tab.Panel className="h-full">
                                <NopPositionTable
                                    setCSVData={setCSVData}
                                    onOrderModal={onOrderModal as any}
                                    prefillValues={prefillValues}
                                    setPrefillValues={setPrefillValues}
                                    orderDisclosure={orderDisclosure}
                                />
                            </Tab.Panel>
                        </Fragment>
                    ) : (
                        <Fragment>
                            {isMarginAccount && shouldUseNopBalance && shouldUseNopProfitLoss ? (
                                <Tab.Panel className="h-full">
                                    <NopPositionTable
                                        setCSVData={setCSVData}
                                        onOrderModal={onOrderModal as any}
                                        prefillValues={prefillValues}
                                        setPrefillValues={setPrefillValues}
                                        orderDisclosure={orderDisclosure}
                                    />
                                </Tab.Panel>
                            ) : (
                                <Tab.Panel className="h-full">
                                    <OpenPositionTable
                                        setCSVData={setCSVData}
                                        onOrderModal={onOrderModal}
                                        prefillValues={prefillValues}
                                        setPrefillValues={setPrefillValues}
                                        orderDisclosure={orderDisclosure}
                                    />
                                </Tab.Panel>
                            )}
                        </Fragment>
                    )}
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
}
